import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Edit2, Trash2, X, Save, Search } from 'lucide-react';
import { supabase } from '../lib/supabase';

const AnosEscolares = () => {
  const [anosEscolares, setAnosEscolares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAno, setEditingAno] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    nome: ''
  });

  // Carregar anos escolares do Supabase
  const fetchAnosEscolares = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('anos_escolares')
        .select('*')
        .order('nome');

      if (error) {
        console.error('Erro ao carregar anos escolares:', error);
        alert('Erro ao carregar anos escolares: ' + error.message);
        return;
      }

      setAnosEscolares(data || []);
    } catch (error) {
      console.error('Erro ao carregar anos escolares:', error);
      alert('Erro ao carregar anos escolares');
    } finally {
      setLoading(false);
    }
  };

  // Carregar anos escolares ao montar o componente
  useEffect(() => {
    fetchAnosEscolares();
  }, []);

  // Criar novo ano escolar
  const createAnoEscolar = async (anoData) => {
    try {
      const { data, error } = await supabase
        .from('anos_escolares')
        .insert([anoData])
        .select();

      if (error) {
        console.error('Erro ao criar ano escolar:', error);
        alert('Erro ao criar ano escolar: ' + error.message);
        return false;
      }

      setAnosEscolares([...anosEscolares, data[0]]);
      return true;
    } catch (error) {
      console.error('Erro ao criar ano escolar:', error);
      alert('Erro ao criar ano escolar');
      return false;
    }
  };

  // Atualizar ano escolar existente
  const updateAnoEscolar = async (id, anoData) => {
    try {
      const { data, error } = await supabase
        .from('anos_escolares')
        .update(anoData)
        .eq('id', id)
        .select();

      if (error) {
        console.error('Erro ao atualizar ano escolar:', error);
        alert('Erro ao atualizar ano escolar: ' + error.message);
        return false;
      }

      setAnosEscolares(anosEscolares.map(ano => 
        ano.id === id ? data[0] : ano
      ));
      return true;
    } catch (error) {
      console.error('Erro ao atualizar ano escolar:', error);
      alert('Erro ao atualizar ano escolar');
      return false;
    }
  };

  // Excluir ano escolar
  const deleteAnoEscolar = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este ano escolar?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('anos_escolares')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Erro ao excluir ano escolar:', error);
        alert('Erro ao excluir ano escolar: ' + error.message);
        return;
      }

      setAnosEscolares(anosEscolares.filter(ano => ano.id !== id));
    } catch (error) {
      console.error('Erro ao excluir ano escolar:', error);
      alert('Erro ao excluir ano escolar');
    }
  };

  // Função para resetar o formulário
  const resetForm = () => {
    setFormData({ nome: '' });
    setEditingAno(null);
  };

  // Abrir modal para novo ano escolar
  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  // Abrir modal para editar ano escolar
  const openEditModal = (ano) => {
    setEditingAno(ano);
    setFormData({
      nome: ano.nome || ''
    });
    setShowModal(true);
  };

  // Fechar modal
  const closeModal = () => {
    setShowModal(false);
    setEditingAno(null);
  };

  // Salvar ano escolar (criar ou atualizar)
  const handleSave = async (e) => {
    e.preventDefault();
    
    // Validações básicas
    if (!formData.nome.trim()) {
      alert('Por favor, preencha o nome do ano escolar');
      return;
    }

    const success = editingAno 
      ? await updateAnoEscolar(editingAno.id, formData)
      : await createAnoEscolar(formData);

    if (success) {
      closeModal();
    }
  };

  // Atualizar campo do formulário
  const updateFormField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Filtrar anos escolares por busca
  const filteredAnosEscolares = anosEscolares.filter(ano => 
    ano.nome?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="anos-escolares">
        <div className="loading-state">
          <p>Carregando anos escolares...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="anos-escolares">
      <div className="page-header">
        <h1 className="page-title">Anos Escolares</h1>
        <p className="page-subtitle">Gerenciamento de séries e níveis de ensino</p>
      </div>

      {/* Card de Estatísticas */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-header">
            <div className="stat-icon">
              <Calendar size={24} />
            </div>
          </div>
          <div className="stat-value">{anosEscolares.length}</div>
          <div className="stat-label">Anos Cadastrados</div>
        </div>
      </div>

      {/* Controles */}
      <div className="controls-section">
        <div className="search-container">
          <Search size={20} />
          <input
            type="text"
            placeholder="Buscar ano escolar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <button 
          onClick={openCreateModal}
          className="btn-primary"
        >
          <Plus size={20} />
          Novo Ano Escolar
        </button>
      </div>

      {/* Lista de Anos Escolares */}
      <div className="content-card">
        {filteredAnosEscolares.length === 0 ? (
          <div className="empty-state">
            <Calendar size={48} />
            <p>Nenhum ano escolar encontrado</p>
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="btn-secondary"
              >
                Limpar busca
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Ano Escolar</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredAnosEscolares.map(ano => (
                <tr key={ano.id}>
                  <td>
                    <div className="ano-info">
                      <span className="ano-nome">{ano.nome}</span>
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        onClick={() => openEditModal(ano)}
                        className="btn-icon btn-edit"
                        title="Editar"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button 
                        onClick={() => deleteAnoEscolar(ano.id)}
                        className="btn-icon btn-delete"
                        title="Excluir"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal de Criação/Edição */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingAno ? 'Editar Ano Escolar' : 'Novo Ano Escolar'}</h2>
              <button 
                onClick={closeModal}
                className="btn-icon"
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSave} className="modal-form">
              <div className="form-group">
                <label htmlFor="nome">Nome do Ano Escolar *</label>
                <input
                  type="text"
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => updateFormField('nome', e.target.value)}
                  placeholder="Ex: 1º Ano, 2º Ano, 3º Ano..."
                  required
                />
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  onClick={closeModal}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button 
                  type="submit"
                  className="btn-primary"
                >
                  <Save size={16} />
                  {editingAno ? 'Atualizar' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        .anos-escolares {
          padding: 24px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .page-header {
          margin-bottom: 32px;
        }

        .page-title {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin: 0 0 8px 0;
        }

        .page-subtitle {
          font-size: 16px;
          color: #64748b;
          margin: 0;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 32px;
        }

        .stat-card {
          background: white;
          padding: 24px;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e2e8f0;
        }

        .stat-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: #3b82f6;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .stat-value {
          font-size: 32px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 4px;
        }

        .stat-label {
          font-size: 14px;
          color: #64748b;
          font-weight: 500;
        }

        .controls-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          gap: 16px;
        }

        .search-container {
          position: relative;
          flex: 1;
          max-width: 400px;
        }

        .search-container svg {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #64748b;
        }

        .search-input {
          width: 100%;
          padding: 12px 12px 12px 44px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          background: white;
        }

        .search-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .btn-primary {
          background: #3b82f6;
          color: white;
          border: none;
          padding: 12px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: background-color 0.2s;
        }

        .btn-primary:hover {
          background: #2563eb;
        }

        .btn-secondary {
          background: #f8fafc;
          color: #475569;
          border: 1px solid #d1d5db;
          padding: 12px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-secondary:hover {
          background: #f1f5f9;
          border-color: #94a3b8;
        }

        .content-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e2e8f0;
          overflow: hidden;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
        }

        .data-table th {
          background: #f8fafc;
          padding: 16px;
          text-align: left;
          font-weight: 600;
          color: #374151;
          border-bottom: 1px solid #e5e7eb;
        }

        .data-table td {
          padding: 16px;
          border-bottom: 1px solid #f3f4f6;
        }

        .data-table tr:hover {
          background: #f9fafb;
        }

        .ano-info {
          display: flex;
          flex-direction: column;
        }

        .ano-nome {
          font-weight: 600;
          color: #1e293b;
        }

        .action-buttons {
          display: flex;
          gap: 8px;
        }

        .btn-icon {
          width: 36px;
          height: 36px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .btn-edit {
          background: #f0f9ff;
          color: #0369a1;
        }

        .btn-edit:hover {
          background: #e0f2fe;
        }

        .btn-delete {
          background: #fef2f2;
          color: #dc2626;
        }

        .btn-delete:hover {
          background: #fee2e2;
        }

        .empty-state {
          text-align: center;
          padding: 48px 24px;
          color: #64748b;
        }

        .empty-state svg {
          margin-bottom: 16px;
          opacity: 0.5;
        }

        .empty-state p {
          margin: 0 0 16px 0;
          font-size: 16px;
        }

        .loading-state {
          text-align: center;
          padding: 48px 24px;
          color: #64748b;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          border-radius: 12px;
          width: 100%;
          max-width: 500px;
          margin: 20px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal-header {
          padding: 24px 24px 0 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .modal-header h2 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
          color: #1e293b;
        }

        .modal-form {
          padding: 0 24px 24px 24px;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group label {
          display: block;
          margin-bottom: 6px;
          font-weight: 600;
          color: #374151;
          font-size: 14px;
        }

        .form-group input {
          width: 100%;
          padding: 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          box-sizing: border-box;
        }

        .form-group input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .modal-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          margin-top: 32px;
        }

        @media (max-width: 768px) {
          .anos-escolares {
            padding: 16px;
          }

          .controls-section {
            flex-direction: column;
            align-items: stretch;
          }

          .search-container {
            max-width: none;
          }

          .data-table {
            font-size: 14px;
          }

          .data-table th,
          .data-table td {
            padding: 12px;
          }

          .modal-content {
            margin: 10px;
          }

          .modal-actions {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
};

export default AnosEscolares;
